import { useState, useCallback, useRef, useEffect } from 'react';
import { buildMjeErrorFeedback } from '@app/modules/mje/api/mjeResponse';

interface SafeExecuteOptions {
  rethrow?: boolean;
  onError?: (error: unknown) => void;
}

export const useRequestState = <T = unknown>() => {
  const [data, setData] = useState<T | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isMountedRef = useRef(true);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const safeExecute = useCallback(
    async (
      requestFunction: () => Promise<T>,
      defaultErrorMessage = 'Ocorreu um erro inesperado',
      options: SafeExecuteOptions = {}
    ): Promise<T | undefined> => {
      setIsLoading(true);
      setError(null);

      try {
        if (isMountedRef.current) {
          setIsLoading(true);
          setError(null);
        }

        const result = await requestFunction();
        if (isMountedRef.current) {
          setData(result);
        }

        return result;
      } catch (err: unknown) {
        console.error(err);
        if (isMountedRef.current) {
          const feedback = buildMjeErrorFeedback(err);
          setError(feedback.backendMessage || defaultErrorMessage);

          options.onError?.(err);

          if (options.rethrow ?? true) {
            throw err;
          }
        }

        return undefined;
      } finally {
        if (isMountedRef.current) {
          setIsLoading(false);
        }
      }
    },
    []
  );

  return { data, isLoading, error, safeExecute };
};
